import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../../components/core/basic/Button";
import { Modal } from "../../../components/core/basic/Modal";
import { css, cx } from "../../../../styled-system/css";
import { TextField } from "../../../components/fields/base/TextField";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { formField } from "../../../../styled-system/recipes";
import { z } from "zod";
import { useCreateUserInviteMutation } from "../api/useCreateUserInviteMutation";
import { ROLE_LABEL_LOOKUP, getRoleLabel } from "./OrgUsersTable";
import { Select } from "../../../components/fields/base/Select";
import { useUserRoleOptions } from "../api/useUserRoleOptions";
import { Label } from "react-aria-components";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import OrgSelectionTable from "../../../components/custom/staff/OrgSelectionTable";
import { type AppColumnDef } from "../../../components/table/tableFeatures";
import { type Organization } from "../../../zodios/common/orgSchema";
import { getOrganizationsOptions } from "../../admin/api/useOrganizations";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { useAppSelector } from "../../../redux/hooks";

const DEFAULT_ROLE_NAME = "modify";
const emailSchema = z.string().email();
const STAFF_EMAIL_DOMAIN = "@gotrellis.com";

const isStaffEmailAddress = (email: string) => email.trim().toLowerCase().endsWith(STAFF_EMAIL_DOMAIN);

type OrgRoleSelection = Record<number, string>;

export const UserInvitationModal = () => {
  const { t } = useTranslation(["user_management"]);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>{t(($) => $.user_management["Invite New User"])}</Button>
      {isOpen && (
        <Modal
          isOpen={true}
          onOpenChange={(updatedIsOpen) => {
            setIsOpen(updatedIsOpen);
          }}
          title={t(($) => $.user_management["Invite New User"])}
          css={{ width: "60vw", padding: "xlarge", textAlign: "center" }}
        >
          <UserInvitationForm
            onClose={() => {
              setIsOpen(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};

const UserInvitationForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation(["user_management", "common"]);
  const [emailAddress, setEmailAddress] = useState("");
  const [orgRoles, setOrgRoles] = useState<OrgRoleSelection>({});
  const [grantStaffAccess, setGrantStaffAccess] = useState(false);

  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);

  const currentUser = useAppSelector((state) => state.user);
  const createUserInvitation = useCreateUserInviteMutation();
  const userRoleOptionsQuery = useUserRoleOptions();
  const organizationsQuery = useQuery(getOrganizationsOptions());

  const classes = formField();

  const roleOptions = useMemo(() => {
    if (!userRoleOptionsQuery.data) {
      return [];
    }
    return userRoleOptionsQuery.data.roles
      .filter((role) => role.role_name in ROLE_LABEL_LOOKUP)
      .map((role) => ({
        label: getRoleLabel(t, role.role_name as keyof typeof ROLE_LABEL_LOOKUP) || role.id.toString(),
        value: role.role_name,
      }))
      .filter((role) => Boolean(role.label));
  }, [userRoleOptionsQuery.data, t]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    createUserInvitation.mutate(
      {
        invite_email: emailAddress,
        login_type: "username_password",
        user_permissions:
          grantStaffAccess ?
            { user_roles: {}, dashboard: true, is_trellis_staff: true }
          : {
              user_roles: orgRoles,
              org_ids: selectedOrgIds,
              dashboard: true,
            },
      },
      {
        onSettled: () => {
          onClose();
        },
      },
    );
  };

  const handleOrgChange = (ids: number[]) => {
    setSelectedOrgIds(ids);

    setOrgRoles((prev) => {
      const updatedOrgRoles = { ...prev };

      ids.forEach((id) => {
        if (!updatedOrgRoles[id]) {
          updatedOrgRoles[id] = DEFAULT_ROLE_NAME;
        }
      });

      Object.keys(updatedOrgRoles).forEach((id) => {
        if (!ids.includes(Number(id))) {
          // oxlint-disable-next-line no-dynamic-delete
          delete updatedOrgRoles[Number(id)];
        }
      });

      return updatedOrgRoles;
    });
  };

  const roleColumn = useMemo<AppColumnDef<Organization>>(
    () => ({
      id: "role",
      header: t(($) => $.user_management["Role"]),
      size: 200,
      minSize: 200,
      maxSize: 200,
      cell: ({ row }) => {
        const orgId = row.original.id;

        if (!selectedOrgIds.includes(orgId)) {
          return null;
        }

        return (
          <Select
            items={roleOptions}
            selectedKey={orgRoles[orgId]}
            onSelectionChange={(value) => {
              if (!value) return;

              setOrgRoles((prev) => ({
                ...prev,
                [orgId]: String(value),
              }));
            }}
            css={{ width: "150px", paddingX: "large" }}
          />
        );
      },
    }),
    [orgRoles, roleOptions, selectedOrgIds, t],
  );

  const isValidEmail = emailSchema.safeParse(emailAddress).success;
  const canGrantStaffAccess = currentUser.is_staff && isStaffEmailAddress(emailAddress);
  const canSubmit = isValidEmail && (grantStaffAccess ? canGrantStaffAccess : Boolean(selectedOrgIds.length));

  return (
    <VStack
      className={cx(
        css({ maxHeight: "70vh", scrollbarGutter: "stable", overflowY: "auto" }),
        "custom-scroll-container",
      )}
    >
      <p className={css({ width: "80%" })}>
        {grantStaffAccess ?
          t(
            ($) =>
              $.user_management[
                "Invite a Trellis staff member. Staff have full access to every organization, so no organization selection is needed."
              ],
          )
        : t(
            ($) =>
              $.user_management[
                "Invite a new user by entering their email address, then selecting the organizations they can access and their role in each. Invited users have 14 days to accept."
              ],
          )
        }
      </p>
      <form
        data-hs-do-not-collect="true"
        onSubmit={handleSubmit}
        className={css({ display: "flex", gap: "medium", flexDirection: "column" })}
      >
        <TextField
          label={t(($) => $.user_management["Email Address"])}
          name={"email"}
          type={"email"}
          value={emailAddress}
          onChange={(value) => {
            setEmailAddress(value);
            if (!isStaffEmailAddress(value)) {
              setGrantStaffAccess(false);
            }
          }}
          css={{ maxWidth: "400px" }}
        />
        {canGrantStaffAccess ?
          <Flex gap={"small"} alignItems={"center"}>
            <Checkbox
              value={grantStaffAccess}
              onChange={(value) => {
                setGrantStaffAccess(value);
              }}
              label={t(($) => $.user_management["Grant Trellis staff access"])}
            />
            <HoverInfo>
              {t(
                ($) =>
                  $.user_management[
                    "Trellis staff get full access to every organization. Access is applied when the invite is accepted."
                  ],
              )}
            </HoverInfo>
          </Flex>
        : null}
        {grantStaffAccess ? null : (
          <>
            <Label className={classes.label}>
              {t(($) => $.user_management["Available Organizations"])}{" "}
              <HoverInfo>
                {t(
                  ($) =>
                    $.user_management[
                      "Specify the organizations the invited user will have access to. This can be updated later."
                    ],
                )}
              </HoverInfo>
            </Label>
            <OrgSelectionTable
              selectedOrgIds={selectedOrgIds}
              onChange={handleOrgChange}
              displaySelectOrgFilters={false}
              additionalColumns={[roleColumn]}
              orgs={organizationsQuery.data ?? []}
              isLoading={organizationsQuery.isLoading}
            />
            {selectedOrgIds.length ?
              <Flex justifyContent={"center"}>
                <p className={css({ maxWidth: "75%", textAlign: "center" })}>
                  {t(
                    ($) =>
                      $.user_management[
                        "Are you sure you want to send an invitation to join the selected organizations?"
                      ],
                    {
                      count: selectedOrgIds.length,
                    },
                  )}
                </p>
              </Flex>
            : null}
          </>
        )}

        <Flex gap={"xlarge"} justifyContent={"center"} paddingBottom={"medium"}>
          <Button
            variant={"secondary"}
            onClick={() => {
              onClose();
            }}
          >
            {t(($) => $.common.actions.Cancel)}
          </Button>
          <Button type={"submit"} isDisabled={!canSubmit} isPending={createUserInvitation.isPending}>
            {t(($) => $.user_management["Send Invitation"])}
          </Button>
        </Flex>
      </form>
    </VStack>
  );
};
